// src/screens/GameProfilesScreen.js
// ============================================================================
// GAME PROFILES - Manage In-Game Names (IGN) for Each Game
// 
// Users set their IGN for each game they play to enable:
// - Automatic player verification in lobbies
// - Match result verification via vision system
// - Accurate leaderboard display
//
// MULTI-PLATFORM SUPPORT:
// - Users can select multiple platforms they play on
// - Each platform can have a different IGN if needed
// - Data structure: { platforms: { 'PC': { ign: 'Name1' }, 'PS5': { ign: 'Name2' } } }
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, ScrollView,
  ActivityIndicator, TextInput, Modal, Platform, Alert
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { getAuth } from 'firebase/auth';
import { collection, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebaseConfig';
import { useError } from '../components/ErrorContext';
import Header from '../components/Header';
import NavBar from '../components/NavBar';

// Import game data from shared constants
import { GAME_PROFILES } from '../constants/gameData';
// Open catalog: browse-catalog games + free-text community games all take IGNs.
import {
  EXTRA_EVENT_GAMES,
  COMMUNITY_ALLOWED_PLATFORMS,
  COMMUNITY_GENRES,
  buildCommunityGameProfile,
  communityGameKeyFromName,
  communityGameNameFromKey,
  isCommunityGameKey,
  normalizeCommunityGameName,
  normalizeCommunityGenre,
} from '../constants/communityGames';
import { PLATFORM_SERVICES, getServicesForHardwarePlatform, GAME_SERVICE_OVERRIDES } from '../constants/platformServices';
import { useGameAvailability } from '../hooks/useGameAvailability';

// ============================================================================
// GAME TYPE ICONS & IGN LABELS
// ============================================================================
const GAME_TYPE_ICONS = {
  fighting: '🥊',
  sports: '🏆',
  shooter: '🎯',
  moba: '🧙',
  rts: '⚔️',
  racing: '🏎️',
};

const GAME_TYPE_LABELS = {
  fighting: 'Fighting',
  sports: 'Sports',
  shooter: 'Shooter',
  moba: 'MOBA',
  rts: 'Strategy',
  racing: 'Racing',
};

// Default IGN labels by game type
const DEFAULT_IGN_LABELS = {
  fighting: 'Fighter ID',
  sports: 'Gamertag',
  shooter: 'Display Name',
  moba: 'Summoner Name',
  rts: 'Player Name',
  racing: 'Driver Name',
};

// Custom IGN labels for specific games (overrides defaults)
const CUSTOM_IGN_LABELS = {
  'sf6': 'Fighter ID',
  'tk8': 'Tekken ID',
  '2xk': 'Riot ID',           // 2XKO - Riot's 2v2 tag fighter
  'cod': 'Activision ID',
  'val': 'Riot ID',
  'apl': 'EA ID',
  'ow2': 'BattleTag',
  'lol': 'Riot ID',
  'efc': 'EA ID',
  'rkl': 'Epic/Platform ID',
};

// Sort categories in a logical order
const CATEGORY_ORDER = ['Fighting', 'Shooter', 'Sports', 'MOBA', 'Strategy', 'Racing', 'More Games', 'Other'];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get the service account link for a given game + hardware platform.
 * Checks GAME_SERVICE_OVERRIDES first, then falls back to hardware platform mapping.
 */
const getServiceLinkForPlatform = (gameId, hardwarePlatform, accounts) => {
  if (!accounts || Object.keys(accounts).length === 0) return null;

  // Check if this game has a required service override
  const overrideServiceId = GAME_SERVICE_OVERRIDES[gameId];
  if (overrideServiceId && accounts[overrideServiceId]) {
    return {
      serviceId: overrideServiceId,
      accountName: accounts[overrideServiceId].accountName,
      service: PLATFORM_SERVICES[overrideServiceId],
    };
  }

  // Find services that cover this hardware platform
  const services = getServicesForHardwarePlatform(hardwarePlatform);
  for (const svc of services) {
    if (accounts[svc.id]) {
      return {
        serviceId: svc.id,
        accountName: accounts[svc.id].accountName,
        service: svc,
      };
    }
  }

  return null;
};

/**
 * Migrate legacy profile data to new multi-platform format
 * Legacy: { ign: 'Name', platform: 'PC' }
 * New: { platforms: { 'PC': { ign: 'Name' } } }
 */
const migrateProfileData = (profile) => {
  if (!profile) return null;
  
  // Already in new format
  if (profile.platforms && typeof profile.platforms === 'object') {
    return profile;
  }
  
  // Migrate from legacy format
  if (profile.ign && profile.platform) {
    return {
      ...profile,
      platforms: {
        [profile.platform]: { ign: profile.ign }
      }
    };
  }
  
  // Legacy format with only IGN (no platform)
  if (profile.ign) {
    return {
      ...profile,
      platforms: {
        'Cross-Platform': { ign: profile.ign }
      }
    };
  }
  
  return profile;
};

/**
 * Get display text for platforms in the game card
 */
const getPlatformDisplayText = (profile) => {
  if (!profile?.platforms) {
    return profile?.platform || null;
  }
  
  const platformNames = Object.keys(profile.platforms);
  if (platformNames.length === 0) return null;
  if (platformNames.length === 1) return platformNames[0];
  if (platformNames.length === 2) return platformNames.join(' & ');
  return `${platformNames.length} platforms`;
};

/**
 * Get primary IGN for display (first platform's IGN or shared IGN)
 */
const getPrimaryIGN = (profile) => {
  if (!profile) return null;
  
  // New format
  if (profile.platforms) {
    const platforms = Object.keys(profile.platforms);
    if (platforms.length > 0) {
      return profile.platforms[platforms[0]]?.ign;
    }
  }
  
  // Legacy format
  return profile.ign;
};

/**
 * Check if all selected platforms have the same IGN
 */
const hasUniformIGN = (platformsData) => {
  const igns = Object.values(platformsData).map(p => p.ign).filter(Boolean);
  if (igns.length === 0) return true;
  return igns.every(ign => ign === igns[0]);
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function GameProfilesScreen({ navigation, route }) {
  const { showError } = useError();
  const isFocused = useIsFocused();
  const functions = getFunctions();
  const { isGameEnabled, loading: availabilityLoading } = useGameAvailability();

  const [profiles, setProfiles] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Modal state - Multi-platform support
  const [selectedPlatforms, setSelectedPlatforms] = useState({}); // { 'PC': { ign: 'Name' }, 'PS5': { ign: 'Name2' } }
  const [useSameIGN, setUseSameIGN] = useState(true); // Toggle for using same IGN across all platforms
  const [sharedIGN, setSharedIGN] = useState(''); // Used when useSameIGN is true

  // Platform accounts (PSN, Xbox, Steam, etc.)
  const [platformAccounts, setPlatformAccounts] = useState({});

  // "Add any game" prompt (open catalog)
  const [customAddVisible, setCustomAddVisible] = useState(false);
  const [customAddName, setCustomAddName] = useState('');
  const [customAddGenre, setCustomAddGenre] = useState('other');
  const [customAddError, setCustomAddError] = useState(null);

  // Arriving from event creation with a typed "Other" game: prefill the
  // add-game prompt so the IGN lands on the SAME cg_* slug the event uses —
  // genre included, or the saved profile mis-categorizes as 'Other'.
  useEffect(() => {
    const prefill = route?.params?.communityGameName;
    if (prefill && typeof prefill === 'string') {
      setCustomAddName(prefill);
      setCustomAddGenre(normalizeCommunityGenre(route?.params?.communityGameGenre));
      setCustomAddError(null);
      setCustomAddVisible(true);
      navigation.setParams({ communityGameName: undefined, communityGameGenre: undefined });
    }
  }, [route?.params?.communityGameName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build game list filtered by admin availability settings
  const { supportedGames, sortedCategories } = useMemo(() => {
    const games = [];
    for (const [gameId, gameData] of Object.entries(GAME_PROFILES)) {
      if (!isGameEnabled(gameId)) continue;
      games.push({
        id: gameId,
        name: gameData.displayName,
        abbr: gameData.abbr,
        type: gameData.type,
        platforms: gameData.allowedPlatforms || [],
        icon: GAME_TYPE_ICONS[gameData.type] || '🎮',
        ignLabel: CUSTOM_IGN_LABELS[gameId] || DEFAULT_IGN_LABELS[gameData.type] || 'In-Game Name',
      });
    }
    // Open catalog: browse-catalog games (box art, generic profile) sort
    // under their REAL genre category (Mario Kart World with Racing);
    // genre-'other' games land in More Games.
    for (const [gameId, extra] of Object.entries(EXTRA_EVENT_GAMES)) {
      const genre = extra.genre || 'other';
      games.push({
        id: gameId,
        name: extra.displayName,
        abbr: extra.abbr,
        type: genre === 'other' ? 'community' : genre,
        isOpenCatalog: true,
        gameGenre: genre,
        // Per-game platform list (MKW is Switch-only); cg_* games below
        // keep the full list.
        platforms: [...(extra.allowedPlatforms || COMMUNITY_ALLOWED_PLATFORMS)],
        icon: GAME_TYPE_ICONS[genre] || '🎮',
        ignLabel: CUSTOM_IGN_LABELS[gameId] || DEFAULT_IGN_LABELS[genre] || 'In-Game Name',
      });
    }
    // ...plus any community game this user already saved an IGN for.
    for (const [profileId, profileData] of Object.entries(profiles)) {
      if (!isCommunityGameKey(profileId)) continue;
      const genre = profileData.gameGenre || 'other';
      games.push({
        id: profileId,
        name: profileData.gameName || communityGameNameFromKey(profileId) || profileId,
        abbr: null,
        type: genre === 'other' ? 'community' : genre,
        isOpenCatalog: true,
        gameGenre: genre,
        platforms: [...COMMUNITY_ALLOWED_PLATFORMS],
        icon: GAME_TYPE_ICONS[genre] || '🎮',
        ignLabel: DEFAULT_IGN_LABELS[genre] || 'In-Game Name',
      });
    }
    games.sort((a, b) => a.name.localeCompare(b.name));

    const categories = games.reduce((acc, game) => {
      const category = game.type === 'community'
        ? 'More Games'
        : (GAME_TYPE_LABELS[game.type] || 'More Games');
      if (!acc[category]) acc[category] = [];
      acc[category].push(game);
      return acc;
    }, {});

    const sorted = CATEGORY_ORDER
      .filter(cat => categories[cat])
      .map(cat => [cat, categories[cat]]);

    return { supportedGames: games, sortedCategories: sorted };
  }, [isGameEnabled, profiles]);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================
  const fetchProfiles = useCallback(async () => {
    try {
      const auth = getAuth();
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      
      const profilesRef = collection(db, 'users', uid, 'gameProfiles');
      const snapshot = await getDocs(profilesRef);
      
      const loadedProfiles = {};
      snapshot.forEach((doc) => {
        // Migrate legacy data to new format
        loadedProfiles[doc.id] = migrateProfileData(doc.data());
      });
      
      setProfiles(loadedProfiles);

      // Also fetch platform accounts for service linking
      try {
        const getPlatformAccounts = httpsCallable(functions, 'getPlatformAccounts');
        const accountsResult = await getPlatformAccounts();
        setPlatformAccounts(accountsResult.data.accounts || {});
      } catch (accountsError) {
        console.warn('Could not load platform accounts:', accountsError.message);
      }
    } catch (error) {
      console.error('Error fetching game profiles:', error);
      showError('Failed to load game profiles');
    } finally {
      setIsLoading(false);
    }
  }, [showError, functions]);
  
  useEffect(() => {
    if (isFocused) {
      fetchProfiles();
    }
  }, [isFocused, fetchProfiles]);
  
  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  const selectedPlatformCount = useMemo(() => {
    return Object.keys(selectedPlatforms).length;
  }, [selectedPlatforms]);
  
  const canSave = useMemo(() => {
    if (selectedPlatformCount === 0) return false;
    
    if (useSameIGN) {
      return sharedIGN.trim().length >= 2;
    }
    
    // Check that all selected platforms have valid IGNs
    return Object.values(selectedPlatforms).every(p => p.ign?.trim()?.length >= 2);
  }, [selectedPlatformCount, useSameIGN, sharedIGN, selectedPlatforms]);
  
  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleEditGame = (game) => {
    const existingProfile = profiles[game.id];
    setSelectedGame(game);

    if (existingProfile?.platforms) {
      // Load existing multi-platform data — dropping platforms the game no
      // longer lists (per-game lists narrowed 2026-08-13: MKW is
      // Switch-only, ...). An off-list entry has no pill, so it could
      // neither be edited nor deselected; the server clears it from the
      // stored doc on the next save.
      const allowed = new Set(game.platforms || []);
      const loaded = {};
      for (const [name, data] of Object.entries(existingProfile.platforms)) {
        if (allowed.size === 0 || allowed.has(name)) loaded[name] = data;
      }
      setSelectedPlatforms(loaded);

      // Check if all IGNs are the same
      const uniform = Object.keys(loaded).length > 0 && hasUniformIGN(loaded);
      setUseSameIGN(Object.keys(loaded).length === 0 ? true : uniform);

      if (uniform) {
        const firstIGN = Object.values(loaded)[0]?.ign || '';
        setSharedIGN(firstIGN);
      } else {
        setSharedIGN('');
      }
    } else {
      // New profile - start fresh
      setSelectedPlatforms({});
      setUseSameIGN(true);
      setSharedIGN('');
    }
  };
  
  const handleCloseModal = () => {
    setSelectedGame(null);
    setSelectedPlatforms({});
    setUseSameIGN(true);
    setSharedIGN('');
  };
  
  const handleTogglePlatform = (platform) => {
    setSelectedPlatforms(prev => {
      const newPlatforms = { ...prev };
      
      if (newPlatforms[platform]) {
        // Remove platform
        delete newPlatforms[platform];
      } else {
        // Add platform with current shared IGN or empty
        newPlatforms[platform] = { ign: useSameIGN ? sharedIGN : '' };
      }
      
      return newPlatforms;
    });
  };
  
  const handleSharedIGNChange = (text) => {
    setSharedIGN(text);
    
    // Update all selected platforms with the shared IGN
    if (useSameIGN) {
      setSelectedPlatforms(prev => {
        const updated = {};
        for (const platform of Object.keys(prev)) {
          updated[platform] = { ign: text };
        }
        return updated;
      });
    }
  };
  
  const handlePlatformIGNChange = (platform, text) => {
    setSelectedPlatforms(prev => ({
      ...prev,
      [platform]: { ign: text }
    }));
  };
  
  const handleToggleUseSameIGN = () => {
    const newValue = !useSameIGN;
    setUseSameIGN(newValue);
    
    if (newValue) {
      // Switching to same IGN - use the first platform's IGN or sharedIGN
      const firstIGN = Object.values(selectedPlatforms)[0]?.ign || sharedIGN;
      setSharedIGN(firstIGN);
      
      // Update all platforms to use the same IGN
      setSelectedPlatforms(prev => {
        const updated = {};
        for (const platform of Object.keys(prev)) {
          updated[platform] = { ign: firstIGN };
        }
        return updated;
      });
    }
  };
  
  const handleSaveProfile = async () => {
    if (!selectedGame) return;
    
    // Validate
    if (selectedPlatformCount === 0) {
      Alert.alert('No Platform Selected', 'Please select at least one platform');
      return;
    }
    
    // Build final platforms data
    let finalPlatforms = {};
    
    if (useSameIGN) {
      const trimmedIGN = sharedIGN.trim();
      
      if (!trimmedIGN) {
        Alert.alert('Invalid IGN', 'Please enter your in-game name');
        return;
      }
      
      if (trimmedIGN.length < 2) {
        Alert.alert('Invalid IGN', 'In-game name must be at least 2 characters');
        return;
      }
      
      if (trimmedIGN.length > 32) {
        Alert.alert('Invalid IGN', 'In-game name must be 32 characters or less');
        return;
      }
      
      for (const platform of Object.keys(selectedPlatforms)) {
        const platformEntry = { ign: trimmedIGN };
        // Auto-link service account if user has one for this platform
        const serviceLink = getServiceLinkForPlatform(selectedGame?.id, platform, platformAccounts);
        if (serviceLink) {
          platformEntry.serviceId = serviceLink.serviceId;
          platformEntry.serviceAccountName = serviceLink.accountName;
        }
        finalPlatforms[platform] = platformEntry;
      }
    } else {
      // Validate each platform's IGN
      for (const [platform, data] of Object.entries(selectedPlatforms)) {
        const trimmedIGN = data.ign?.trim() || '';

        if (!trimmedIGN) {
          Alert.alert('Invalid IGN', `Please enter your in-game name for ${platform}`);
          return;
        }

        if (trimmedIGN.length < 2) {
          Alert.alert('Invalid IGN', `In-game name for ${platform} must be at least 2 characters`);
          return;
        }

        if (trimmedIGN.length > 32) {
          Alert.alert('Invalid IGN', `In-game name for ${platform} must be 32 characters or less`);
          return;
        }

        const platformEntry = { ign: trimmedIGN };
        const serviceLink = getServiceLinkForPlatform(selectedGame?.id, platform, platformAccounts);
        if (serviceLink) {
          platformEntry.serviceId = serviceLink.serviceId;
          platformEntry.serviceAccountName = serviceLink.accountName;
        }
        finalPlatforms[platform] = platformEntry;
      }
    }
    
    setIsSaving(true);
    
    try {
      const updateGameProfile = httpsCallable(functions, 'updateGameProfile');
      
      // Get primary platform and IGN for backward compatibility
      const primaryPlatform = Object.keys(finalPlatforms)[0];
      const primaryIGN = finalPlatforms[primaryPlatform].ign;
      
      await updateGameProfile({
        gameId: selectedGame.id,
        ign: primaryIGN, // Legacy field for backward compatibility
        platform: primaryPlatform, // Legacy field for backward compatibility
        platforms: finalPlatforms, // New multi-platform data
        // Open-catalog games carry their display name + genre so lists can
        // render them without a catalog lookup (ignored for full-profile
        // games; catalog genres are canonical server-side).
        ...(selectedGame.isOpenCatalog || selectedGame.type === 'community'
          ? {
            gameName: selectedGame.gameName || selectedGame.name,
            gameGenre: selectedGame.gameGenre || 'other',
          }
          : {}),
      });
      
      // Update local state
      setProfiles(prev => ({
        ...prev,
        [selectedGame.id]: {
          ign: primaryIGN,
          platform: primaryPlatform,
          platforms: finalPlatforms,
          gameId: selectedGame.id,
          updatedAt: new Date().toISOString(),
        }
      }));
      
      handleCloseModal();
      
      const platformCount = Object.keys(finalPlatforms).length;
      Alert.alert(
        'Profile Saved',
        `Your ${selectedGame.name} profile has been updated for ${platformCount} platform${platformCount > 1 ? 's' : ''}.`
      );
      
    } catch (error) {
      console.error('Error saving game profile:', error);
      showError(error.message || 'Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleDeleteProfile = async () => {
    if (!selectedGame) return;
    
    Alert.alert(
      'Remove Profile',
      `Are you sure you want to remove your ${selectedGame.name} profile from all platforms?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            setIsSaving(true);
            try {
              const deleteGameProfile = httpsCallable(functions, 'deleteGameProfile');
              await deleteGameProfile({ gameId: selectedGame.id });
              
              // Update local state
              setProfiles(prev => {
                const newProfiles = { ...prev };
                delete newProfiles[selectedGame.id];
                return newProfiles;
              });
              
              handleCloseModal();
              
            } catch (error) {
              console.error('Error deleting game profile:', error);
              showError(error.message || 'Failed to remove profile');
            } finally {
              setIsSaving(false);
            }
          }
        }
      ]
    );
  };
  
  // ============================================================================
  // RENDER
  // ============================================================================
  if (isLoading || availabilityLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#FCD000" />
        <Text style={styles.loadingText}>Loading game profiles...</Text>
      </View>
    );
  }

  const configuredCount = Object.keys(profiles).filter(id => isGameEnabled(id)).length;
  const totalGames = supportedGames.length;
  
  return (
    <View style={styles.container}>
      <Header />
      
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        {/* Back Button */}
        <TouchableOpacity 
          style={styles.backButton} 
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}
        >
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Game Profiles</Text>
          <Text style={styles.subtitle}>
            Set your in-game name for each game to enable automatic match verification on supported games
          </Text>
          <View style={styles.statsRow}>
            <Text style={styles.statsText}>
              {configuredCount} of {totalGames} games configured
            </Text>
          </View>
        </View>
        
        {/* Info Banner */}
        <View style={styles.infoBanner}>
          <Text style={styles.infoIcon}>ℹ️</Text>
          <Text style={styles.infoText}>
            Your IGN is used to verify you're in the correct match. Make sure it exactly matches your in-game display name. You can set different names for different platforms.
          </Text>
        </View>
        
        {/* Game Categories */}
        {sortedCategories.map(([category, games]) => (
          <View key={category} style={styles.categorySection}>
            <Text style={styles.categoryTitle}>{category}</Text>
            
            {games.map(game => {
              const profile = profiles[game.id];
              const primaryIGN = getPrimaryIGN(profile);
              const isConfigured = !!primaryIGN;
              const platformDisplay = getPlatformDisplayText(profile);
              
              return (
                <TouchableOpacity
                  key={game.id}
                  style={[styles.gameCard, isConfigured && styles.gameCardConfigured]}
                  onPress={() => handleEditGame(game)}
                >
                  <Text style={styles.gameIcon}>{game.icon}</Text>
                  
                  <View style={styles.gameInfo}>
                    <View style={styles.gameNameRow}>
                      <Text style={styles.gameName}>{game.name}</Text>
                      <Text style={styles.gameAbbr}>{game.abbr}</Text>
                    </View>
                    {isConfigured ? (
                      <>
                        <Text style={styles.ignText}>{primaryIGN}</Text>
                        {platformDisplay && (
                          <Text style={styles.platformText}>{platformDisplay}</Text>
                        )}
                      </>
                    ) : (
                      <Text style={styles.notConfiguredText}>Tap to configure</Text>
                    )}
                  </View>
                  
                  <View style={styles.statusIndicator}>
                    {isConfigured ? (
                      <Text style={styles.checkmark}>✓</Text>
                    ) : (
                      <Text style={styles.arrow}>→</Text>
                    )}
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        ))}

        {/* Open catalog: any game not listed above still takes an IGN — it
            shares the same slugged key event creation uses, so two people
            typing the same game land on the same profile id. */}
        <View style={styles.categorySection}>
          <Text style={styles.categoryTitle}>Playing something else?</Text>
          <TouchableOpacity
            style={styles.gameCard}
            onPress={() => { setCustomAddName(''); setCustomAddVisible(true); }}
          >
            <Text style={styles.gameIcon}>➕</Text>
            <View style={styles.gameInfo}>
              <View style={styles.gameNameRow}>
                <Text style={styles.gameName}>Add any game</Text>
              </View>
              <Text style={styles.notConfiguredText}>
                Name the game, set your in-game name, and it's playable in events
              </Text>
            </View>
            <View style={styles.statusIndicator}>
              <Text style={styles.arrow}>→</Text>
            </View>
          </TouchableOpacity>
        </View>

        <View style={{ height: 100 }} />
      </ScrollView>

      {/* Name prompt for "Add any game" */}
      <Modal
        visible={customAddVisible}
        animationType="fade"
        transparent={true}
        onRequestClose={() => setCustomAddVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { maxWidth: 420, alignSelf: 'center', width: '92%' }]}>
            <Text style={styles.modalTitle}>What game are you playing?</Text>
            <TextInput
              style={{
                backgroundColor: '#161616', borderColor: '#2a2a2a', borderWidth: 1,
                borderRadius: 8, color: '#EDEDED', fontSize: 14,
                paddingVertical: 10, paddingHorizontal: 12, marginTop: 14,
              }}
              value={customAddName}
              onChangeText={(t) => { setCustomAddName(t); setCustomAddError(null); }}
              placeholder="Game name"
              placeholderTextColor="#666"
              maxLength={50}
              autoFocus
            />
            {customAddError ? (
              <Text style={{ color: '#F44336', fontSize: 12.5, marginTop: 8 }}>{customAddError}</Text>
            ) : null}
            {/* Genre matches the game to its category and event modes. */}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
              {COMMUNITY_GENRES.map((g) => (
                <TouchableOpacity
                  key={g.key}
                  onPress={() => setCustomAddGenre(g.key)}
                  style={{
                    borderRadius: 16, borderWidth: 1, paddingVertical: 6, paddingHorizontal: 12,
                    borderColor: customAddGenre === g.key ? '#FCD000' : '#2a2a2a',
                    backgroundColor: customAddGenre === g.key ? '#1a1604' : '#161616',
                  }}
                >
                  <Text style={{
                    color: customAddGenre === g.key ? '#FCD000' : '#999',
                    fontSize: 12.5, fontWeight: '700',
                  }}>
                    {g.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            <View style={{ flexDirection: 'row', gap: 10, marginTop: 16 }}>
              <TouchableOpacity
                style={[styles.deleteButton, { flex: 1 }]}
                onPress={() => setCustomAddVisible(false)}
              >
                <Text style={styles.deleteButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.saveButton, { flex: 1 }]}
                onPress={() => {
                  const name = normalizeCommunityGameName(customAddName);
                  const slug = communityGameKeyFromName(name);
                  if (!slug) {
                    // Inline error — Alert.alert renders nothing on web.
                    setCustomAddError('Name the game with letters or numbers so players can find it.');
                    return;
                  }
                  // Known games resolve to their canonical entry — typed
                  // "mario kart" lands on the Mario Kart World profile with
                  // its real name, genre, and platform list.
                  const prof = buildCommunityGameProfile(slug, name, customAddGenre);
                  setCustomAddVisible(false);
                  handleEditGame({
                    id: slug,
                    name: prof.displayName,
                    abbr: prof.abbr || null,
                    type: prof.genre === 'other' ? 'community' : prof.genre,
                    isOpenCatalog: true,
                    gameGenre: prof.genre,
                    platforms: [...prof.allowedPlatforms],
                    icon: '🎮',
                    ignLabel: 'In-Game Name',
                    gameName: prof.displayName,
                  });
                }}
              >
                <Text style={styles.saveButtonText}>Continue</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      
      {/* Edit Modal */}
      <Modal
        visible={!!selectedGame}
        animationType="slide"
        transparent={true}
        onRequestClose={handleCloseModal}
      >
        <View style={styles.modalOverlay}>
          <ScrollView 
            style={styles.modalScrollView}
            contentContainerStyle={styles.modalScrollContent}
            keyboardShouldPersistTaps="handled"
          >
            <View style={styles.modalContent}>
              {/* Modal Header */}
              <View style={styles.modalHeader}>
                <Text style={styles.modalIcon}>{selectedGame?.icon}</Text>
                <View style={styles.modalTitleContainer}>
                  <Text style={styles.modalTitle}>{selectedGame?.name}</Text>
                  <Text style={styles.modalAbbr}>{selectedGame?.abbr}</Text>
                </View>
                <TouchableOpacity onPress={handleCloseModal} style={styles.closeButton}>
                  <Text style={styles.closeButtonText}>✕</Text>
                </TouchableOpacity>
              </View>
              
              {/* Platform Selection - Multi-Select */}
              {selectedGame?.platforms?.length > 0 && (
                <View style={styles.inputSection}>
                  <Text style={styles.inputLabel}>
                    Platforms
                    <Text style={styles.inputLabelHint}> (select all that apply)</Text>
                  </Text>
                  <View style={styles.platformGrid}>
                    {selectedGame.platforms.map(platform => {
                      const isSelected = !!selectedPlatforms[platform];
                      return (
                        <TouchableOpacity
                          key={platform}
                          style={[
                            styles.platformButton,
                            isSelected && styles.platformButtonSelected
                          ]}
                          onPress={() => handleTogglePlatform(platform)}
                        >
                          <View style={styles.platformButtonContent}>
                            <View style={[
                              styles.checkbox,
                              isSelected && styles.checkboxSelected
                            ]}>
                              {isSelected && <Text style={styles.checkboxCheck}>✓</Text>}
                            </View>
                            <Text style={[
                              styles.platformButtonText,
                              isSelected && styles.platformButtonTextSelected
                            ]}>
                              {platform}
                            </Text>
                          </View>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                  {selectedPlatformCount > 0 && (
                    <Text style={styles.platformCountText}>
                      {selectedPlatformCount} platform{selectedPlatformCount > 1 ? 's' : ''} selected
                    </Text>
                  )}
                </View>
              )}
              
              {/* Linked Service Account Badge */}
              {selectedPlatformCount > 0 && (() => {
                // Collect unique service links for selected platforms
                const serviceLinks = {};
                for (const platform of Object.keys(selectedPlatforms)) {
                  const link = getServiceLinkForPlatform(selectedGame?.id, platform, platformAccounts);
                  if (link && !serviceLinks[link.serviceId]) {
                    serviceLinks[link.serviceId] = link;
                  }
                }
                const links = Object.values(serviceLinks);
                if (links.length === 0) return null;

                return (
                  <View style={styles.serviceLinkSection}>
                    {links.map(link => (
                      <View key={link.serviceId} style={styles.serviceLinkBadge}>
                        <Text style={styles.serviceLinkIcon}>{'🔗'}</Text>
                        <Text style={styles.serviceLinkText}>
                          {link.service?.shortName}: {link.accountName}
                        </Text>
                      </View>
                    ))}
                  </View>
                );
              })()}

              {/* IGN Input Section */}
              {selectedPlatformCount > 0 && (
                <>
                  {/* Toggle for same/different IGN */}
                  {selectedPlatformCount > 1 && (
                    <View style={styles.ignToggleSection}>
                      <TouchableOpacity
                        style={styles.ignToggleButton}
                        onPress={handleToggleUseSameIGN}
                      >
                        <View style={[
                          styles.toggleSwitch,
                          !useSameIGN && styles.toggleSwitchActive
                        ]}>
                          <View style={[
                            styles.toggleKnob,
                            !useSameIGN && styles.toggleKnobActive
                          ]} />
                        </View>
                        <Text style={styles.ignToggleText}>
                          {useSameIGN 
                            ? 'Same name on all platforms' 
                            : 'Different names per platform'}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  )}
                  
                  {/* Shared IGN Input (when useSameIGN is true) */}
                  {useSameIGN ? (
                    <View style={styles.inputSection}>
                      <Text style={styles.inputLabel}>
                        {selectedGame?.ignLabel || 'In-Game Name'}
                      </Text>
                      <TextInput
                        style={styles.textInput}
                        placeholder={`Enter your ${selectedGame?.ignLabel || 'IGN'}`}
                        placeholderTextColor="#666"
                        value={sharedIGN}
                        onChangeText={handleSharedIGNChange}
                        autoCapitalize="none"
                        autoCorrect={false}
                        maxLength={32}
                      />
                      <Text style={styles.inputHint}>
                        This must exactly match your in-game display name
                      </Text>
                    </View>
                  ) : (
                    /* Per-Platform IGN Inputs (when useSameIGN is false) */
                    <View style={styles.inputSection}>
                      <Text style={styles.inputLabel}>
                        {selectedGame?.ignLabel || 'In-Game Name'} per Platform
                      </Text>
                      {Object.keys(selectedPlatforms).map(platform => (
                        <View key={platform} style={styles.platformIGNRow}>
                          <Text style={styles.platformIGNLabel}>{platform}</Text>
                          <TextInput
                            style={styles.platformIGNInput}
                            placeholder={`Enter ${platform} name`}
                            placeholderTextColor="#666"
                            value={selectedPlatforms[platform]?.ign || ''}
                            onChangeText={(text) => handlePlatformIGNChange(platform, text)}
                            autoCapitalize="none"
                            autoCorrect={false}
                            maxLength={32}
                          />
                        </View>
                      ))}
                      <Text style={styles.inputHint}>
                        Each name must exactly match your display name on that platform
                      </Text>
                    </View>
                  )}
                </>
              )}
              
              {/* Empty State */}
              {selectedPlatformCount === 0 && (
                <View style={styles.emptyPlatformState}>
                  <Text style={styles.emptyPlatformText}>
                    Select at least one platform to continue
                  </Text>
                </View>
              )}
              
              {/* Action Buttons */}
              <View style={styles.modalActions}>
                {profiles[selectedGame?.id] && (
                  <TouchableOpacity
                    style={styles.deleteButton}
                    onPress={handleDeleteProfile}
                    disabled={isSaving}
                  >
                    <Text style={styles.deleteButtonText}>Remove</Text>
                  </TouchableOpacity>
                )}
                
                <TouchableOpacity
                  style={[
                    styles.saveButton, 
                    (!canSave || isSaving) && styles.saveButtonDisabled
                  ]}
                  onPress={handleSaveProfile}
                  disabled={!canSave || isSaving}
                >
                  {isSaving ? (
                    <ActivityIndicator size="small" color="#000" />
                  ) : (
                    <Text style={styles.saveButtonText}>Save Profile</Text>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          </ScrollView>
        </View>
      </Modal>
      
      <NavBar navigation={navigation} />
    </View>
  );
}

// ============================================================================
// STYLES
// ============================================================================
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0D0D12',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 100,
  },
  
  // Back Button
  backButton: {
    alignSelf: 'flex-start',
    paddingVertical: 10,
    paddingHorizontal: 16,
    marginBottom: 16,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#2A2A36',
    backgroundColor: '#1A1A24',
  },
  backButtonText: {
    color: '#FCD000',
    fontSize: 15,
    fontWeight: '700',
  },
  
  // Loading
  loadingContainer: {
    flex: 1,
    backgroundColor: '#0D0D12',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#888',
    marginTop: 12,
    fontSize: 14,
  },
  
  // Header
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: '800',
    color: '#FCD000',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    color: '#888',
    lineHeight: 20,
  },
  statsRow: {
    marginTop: 12,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#1A1A24',
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  statsText: {
    color: '#FCD000',
    fontSize: 13,
    fontWeight: '600',
  },
  
  // Info Banner
  infoBanner: {
    flexDirection: 'row',
    backgroundColor: '#1A2B4A',
    borderRadius: 10,
    padding: 12,
    marginBottom: 24,
    alignItems: 'flex-start',
  },
  infoIcon: {
    fontSize: 16,
    marginRight: 10,
  },
  infoText: {
    flex: 1,
    color: '#8AB4F8',
    fontSize: 13,
    lineHeight: 18,
  },
  
  // Category Section
  categorySection: {
    marginBottom: 24,
  },
  categoryTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#888',
    marginBottom: 12,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  
  // Game Card
  gameCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1A1A24',
    borderRadius: 12,
    padding: 14,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#2A2A36',
  },
  gameCardConfigured: {
    borderColor: '#2D4A2D',
    backgroundColor: '#1A2420',
  },
  gameIcon: {
    fontSize: 28,
    marginRight: 14,
  },
  gameInfo: {
    flex: 1,
  },
  gameNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  gameName: {
    fontSize: 16,
    fontWeight: '700',
    color: '#FFF',
  },
  gameAbbr: {
    fontSize: 12,
    color: '#666',
    marginLeft: 8,
    fontWeight: '600',
  },
  ignText: {
    fontSize: 14,
    color: '#FCD000',
    fontWeight: '600',
  },
  platformText: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
  },
  notConfiguredText: {
    fontSize: 13,
    color: '#666',
    fontStyle: 'italic',
  },
  statusIndicator: {
    width: 30,
    alignItems: 'center',
  },
  checkmark: {
    fontSize: 18,
    color: '#4CAF50',
    fontWeight: 'bold',
  },
  arrow: {
    fontSize: 18,
    color: '#666',
  },
  
  // Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.85)',
    justifyContent: 'flex-end',
  },
  modalScrollView: {
    maxHeight: '90%',
  },
  modalScrollContent: {
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#1A1A24',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 24,
    paddingBottom: Platform.OS === 'ios' ? 40 : 24,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
  },
  modalIcon: {
    fontSize: 32,
    marginRight: 12,
  },
  modalTitleContainer: {
    flex: 1,
  },
  modalTitle: {
    fontSize: 22,
    fontWeight: '800',
    color: '#FFF',
  },
  modalAbbr: {
    fontSize: 13,
    color: '#888',
    fontWeight: '600',
  },
  closeButton: {
    padding: 8,
  },
  closeButtonText: {
    fontSize: 20,
    color: '#888',
  },
  
  // Input Section
  inputSection: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFF',
    marginBottom: 8,
  },
  inputLabelHint: {
    fontSize: 12,
    fontWeight: '400',
    color: '#888',
  },
  textInput: {
    backgroundColor: '#0D0D12',
    borderWidth: 1,
    borderColor: '#2A2A36',
    borderRadius: 10,
    padding: 14,
    fontSize: 16,
    color: '#FFF',
  },
  inputHint: {
    fontSize: 12,
    color: '#666',
    marginTop: 6,
  },
  
  // Platform Grid (Multi-Select)
  platformGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  platformButton: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: '#0D0D12',
    borderWidth: 1,
    borderColor: '#2A2A36',
    borderRadius: 8,
    marginBottom: 4,
  },
  platformButtonSelected: {
    backgroundColor: 'rgba(252, 208, 0, 0.15)',
    borderColor: '#FCD000',
  },
  platformButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#666',
    marginRight: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    backgroundColor: '#FCD000',
    borderColor: '#FCD000',
  },
  checkboxCheck: {
    fontSize: 12,
    color: '#000',
    fontWeight: 'bold',
  },
  platformButtonText: {
    fontSize: 13,
    color: '#888',
    fontWeight: '500',
  },
  platformButtonTextSelected: {
    color: '#FCD000',
    fontWeight: '700',
  },
  platformCountText: {
    fontSize: 12,
    color: '#FCD000',
    marginTop: 8,
    fontWeight: '500',
  },
  serviceLinkSection: {
    marginBottom: 12,
  },
  serviceLinkBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1A2A1A',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 6,
    borderWidth: 1,
    borderColor: '#2ECC7140',
  },
  serviceLinkIcon: {
    fontSize: 14,
    marginRight: 8,
  },
  serviceLinkText: {
    color: '#2ECC71',
    fontSize: 13,
    fontWeight: '600',
  },
  
  // IGN Toggle Section
  ignToggleSection: {
    marginBottom: 16,
  },
  ignToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    backgroundColor: '#0D0D12',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#2A2A36',
  },
  toggleSwitch: {
    width: 44,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#2A2A36',
    padding: 2,
    marginRight: 12,
  },
  toggleSwitchActive: {
    backgroundColor: '#FCD000',
  },
  toggleKnob: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#888',
  },
  toggleKnobActive: {
    backgroundColor: '#000',
    transform: [{ translateX: 20 }],
  },
  ignToggleText: {
    flex: 1,
    fontSize: 14,
    color: '#FFF',
  },
  
  // Per-Platform IGN Inputs
  platformIGNRow: {
    marginBottom: 12,
  },
  platformIGNLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FCD000',
    marginBottom: 6,
  },
  platformIGNInput: {
    backgroundColor: '#0D0D12',
    borderWidth: 1,
    borderColor: '#2A2A36',
    borderRadius: 10,
    padding: 12,
    fontSize: 15,
    color: '#FFF',
  },
  
  // Empty Platform State
  emptyPlatformState: {
    padding: 24,
    alignItems: 'center',
  },
  emptyPlatformText: {
    fontSize: 14,
    color: '#888',
    textAlign: 'center',
  },
  
  // Modal Actions
  modalActions: {
    flexDirection: 'row',
    marginTop: 24,
    gap: 12,
  },
  deleteButton: {
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderWidth: 1,
    borderColor: '#FF4444',
    borderRadius: 10,
  },
  deleteButtonText: {
    color: '#FF4444',
    fontSize: 15,
    fontWeight: '600',
  },
  saveButton: {
    flex: 1,
    backgroundColor: '#FCD000',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    color: '#000',
    fontSize: 16,
    fontWeight: '700',
  },
});